import type { ColumnDef, Row } from "@tanstack/react-table";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import Table, { buildActionColumn, TableControls } from "@/components/Table";
import type { Action } from "@/components/MenuButton";
import PersonProfileDocumentFields, {
  emptyPersonProfileDocumentForm,
  getInitialPersonProfileDocumentForm,
  isProfileDocumentHiddenFromViewer,
  resolvedPrivacyRestricted,
  type PersonProfileDocumentFormState
} from "@/features/person/components/PersonProfileDocumentFields";
const PrintTranscriptSheet = lazy(() => import("@/features/person/components/PrintTranscript/PrintTranscriptSheet"));
import { useCanManagePeople } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import useTableRowMenu, { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import { showSnackbar } from "@/lib/snackbarUtils";
import {
  CREATE_PERSON_PROFILE_DOCUMENT_MUTATION,
  DELETE_PERSON_PROFILE_DOCUMENT_MUTATION,
  UPDATE_PERSON_PROFILE_DOCUMENT_MUTATION
} from "./mutations";
import {
  PERSON_PROFILE_DOCUMENT_LIBRARY_QUERY,
  type PersonProfileDocument,
  type PersonProfileDocumentCategory
} from "./queries";

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; document: PersonProfileDocument }
  | { mode: "delete"; document: PersonProfileDocument };

function categoryLabel(category: string, categories: PersonProfileDocumentCategory[]) {
  return categories.find((option) => option.value === category)?.label ?? category;
}

function documentMatchesSearch(document: PersonProfileDocument, query: string) {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  return [document.name, document.filename, document.description]
    .filter((value): value is string => !!value)
    .some((value) => value.toLowerCase().includes(needle));
}

function getColumns(
  categories: PersonProfileDocumentCategory[],
  setMenu: (menu: TableRowMenu) => void,
  onEdit: (document: PersonProfileDocument) => void,
  onDelete: (document: PersonProfileDocument) => void,
  canManage: boolean
): ColumnDef<PersonProfileDocument>[] {
  const columns: ColumnDef<PersonProfileDocument>[] = [
    {
      header: "Name",
      accessorKey: "name",
      enableSorting: true,
      size: 280,
      sortingFn: "customAlphanumericSorting"
    },
    {
      header: "Filename",
      accessorKey: "filename",
      enableSorting: true,
      size: 220,
      sortingFn: "customAlphanumericSorting",
      cell: ({ row }) => row.original.filename || "--"
    },
    {
      header: "Category",
      accessorFn: (row) => categoryLabel(row.category, categories),
      enableSorting: true,
      size: 220,
      sortingFn: "customAlphanumericSorting"
    },
    {
      header: "Privacy",
      accessorKey: "privacyRestricted",
      enableSorting: true,
      size: 140,
      cell: ({ row }) => (row.original.privacyRestricted ? "Private" : "All staff")
    },
    {
      header: "Added",
      accessorKey: "createdAt",
      enableSorting: true,
      size: 140,
      cell: ({ row }) => toFormat(row.original.createdAt) || "--"
    }
  ];

  if (!canManage) return columns;

  return [
    ...columns,
    buildActionColumn<PersonProfileDocument>({
      onEditClick: (row) => onEdit(row),
      editButtonTitle: "Edit Document",
      getActions: (row) => [
        { text: "Delete Document", iconName: "delete", destructive: true, onClick: () => onDelete(row) }
      ],
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];
}

function DocumentFormDialog({
  open,
  mode,
  personId,
  pgaId,
  document,
  categories,
  onClose
}: {
  open: boolean;
  mode: "create" | "edit";
  personId?: string;
  pgaId: string;
  document?: PersonProfileDocument;
  categories: PersonProfileDocumentCategory[];
  onClose: () => void;
}) {
  const [form, setForm] = useState<PersonProfileDocumentFormState>(emptyPersonProfileDocumentForm);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fileError, setFileError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(getInitialPersonProfileDocumentForm(mode === "edit" ? document : undefined));
      setSaveError(null);
      setFileError("");
    }
  }, [document, mode, open]);

  const refetchLibrary = { query: PERSON_PROFILE_DOCUMENT_LIBRARY_QUERY, variables: { pgaId } };

  const [createDocument, { loading: creating }] = useMutation(CREATE_PERSON_PROFILE_DOCUMENT_MUTATION, {
    refetchQueries: [refetchLibrary],
    awaitRefetchQueries: true
  });
  const [updateDocument, { loading: updating }] = useMutation(UPDATE_PERSON_PROFILE_DOCUMENT_MUTATION, {
    refetchQueries: [refetchLibrary],
    awaitRefetchQueries: true
  });

  const loading = creating || updating;
  const canSave =
    !!form.name.trim() && !!form.category && (mode === "edit" || (!!form.file && !!personId)) && !loading && !fileError;

  const handleSave = async () => {
    if (!canSave) return;

    setSaveError(null);

    try {
      if (mode === "create" && personId && form.file) {
        const { data } = await createDocument({
          variables: {
            input: {
              personId,
              name: form.name.trim(),
              description: form.description.trim() || null,
              category: form.category,
              document: form.file,
              privacyRestricted: resolvedPrivacyRestricted(form)
            }
          }
        });
        if (data?.createPersonProfileDocument.success) {
          showSnackbar("Document uploaded");
          onClose();
        } else {
          setSaveError(data?.createPersonProfileDocument.message ?? "Unable to upload document");
        }
      } else if (document) {
        const { data } = await updateDocument({
          variables: {
            input: {
              id: document.id,
              name: form.name.trim(),
              description: form.description.trim() || null,
              category: form.category,
              privacyRestricted: resolvedPrivacyRestricted(form)
            }
          }
        });
        if (data?.updatePersonProfileDocument.success) {
          showSnackbar("Document updated");
          onClose();
        } else {
          setSaveError(data?.updatePersonProfileDocument.message ?? "Unable to update document");
        }
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Unable to save document");
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      headline={mode === "create" ? "Upload Document" : "Edit Document"}
      onClosed={onClose}
      hasFormElements
      displayOverflow
      className="w-[640px] max-w-[95vw]"
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="document-dialog-cancel">
            Cancel
          </Button>
          <Button variant="filled" onClick={handleSave} disabled={!canSave} testId="document-dialog-save">
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {saveError && <Banner variant="error" description={saveError} inline closable={false} hideSecondaryDismiss />}
        <PersonProfileDocumentFields
          mode={mode}
          form={form}
          onChange={setForm}
          categories={categories}
          currentCategory={document?.category}
          fileError={fileError}
          onFileError={setFileError}
        />
      </div>
    </Dialog>
  );
}

function DeleteDocumentDialog({
  open,
  document,
  pgaId,
  onClose
}: {
  open: boolean;
  document?: PersonProfileDocument;
  pgaId: string;
  onClose: () => void;
}) {
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteDocument, { loading }] = useMutation(DELETE_PERSON_PROFILE_DOCUMENT_MUTATION, {
    refetchQueries: [{ query: PERSON_PROFILE_DOCUMENT_LIBRARY_QUERY, variables: { pgaId } }],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    if (open) setDeleteError(null);
  }, [open]);

  const handleConfirm = async () => {
    if (!document) return;

    setDeleteError(null);

    try {
      const { data } = await deleteDocument({ variables: { input: { id: document.id } } });
      if (data?.deletePersonProfileDocument.success) {
        showSnackbar("Document deleted");
        onClose();
      } else {
        setDeleteError(data?.deletePersonProfileDocument.message ?? "Unable to delete document");
      }
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : "Unable to delete document");
    }
  };

  return (
    <Dialog
      open={open}
      headline="Delete document?"
      onClosed={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="delete-document-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            onClick={handleConfirm}
            disabled={loading || !document}
            testId="delete-document-confirm"
          >
            Delete
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {deleteError && (
          <Banner variant="error" description={deleteError} inline closable={false} hideSecondaryDismiss />
        )}
        <p className="text-body-medium">
          This will remove {document?.name ? <strong>{document.name}</strong> : "this document"} from the person
          profile. This cannot be undone.
        </p>
      </div>
    </Dialog>
  );
}

export default function PersonDocuments() {
  const { pgaId } = useParams();
  const flags = useFlags();
  const libraryEnabled = !!flags["backoffice-person-profile-documents"];
  const canPrintTranscript = flags["backoffice-pgm-transcript-print"] === true;
  const canManage = useCanManagePeople();
  const { Menu, setMenu } = useTableRowMenu();
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const [printTranscript, setPrintTranscript] = useState(false);
  const closePrintTranscript = useCallback(() => setPrintTranscript(false), []);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const { data, loading, error, refetch } = useQuery(PERSON_PROFILE_DOCUMENT_LIBRARY_QUERY, {
    variables: { pgaId: pgaId || "" },
    skip: !pgaId || !libraryEnabled
  });

  const categories = data?.personProfileDocumentCategories;
  const documents = useMemo(() => {
    const rows = data?.person?.profileDocuments ?? [];
    return rows.filter(
      (row) =>
        !isProfileDocumentHiddenFromViewer(row) &&
        (categoryFilter === "all" || row.category === categoryFilter) &&
        documentMatchesSearch(row, searchQuery)
    );
  }, [categoryFilter, data?.person?.profileDocuments, searchQuery]);

  const columns = useMemo(
    () =>
      getColumns(
        categories ?? [],
        setMenu,
        (document) => setDialog({ mode: "edit", document }),
        (document) => setDialog({ mode: "delete", document }),
        canManage
      ),
    [canManage, categories, setMenu]
  );

  const handleRowClick = (row: Row<PersonProfileDocument>) => {
    const url = row.original.url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const headingActions = useMemo((): Action[] => {
    const actions: Action[] = [];

    if (canManage) {
      actions.push({
        text: "Upload",
        iconName: "upload",
        variant: "filled",
        onClick: () => setDialog({ mode: "create" }),
        testId: "upload-document"
      });
    }

    if (canPrintTranscript) {
      actions.push({
        text: "Generate Transcript",
        iconName: "print",
        variant: "outlined",
        onClick: () => setPrintTranscript(true),
        testId: "print-transcript"
      });
    }

    return actions;
  }, [canManage, canPrintTranscript]);

  if (!libraryEnabled) {
    return <Navigate to={`/people/${pgaId}/person/overview`} replace />;
  }

  return (
    <>
      <Menu />
      <div className="pt-12" />
      {error ? (
        <ErrorBoundary
          variant="inline"
          title="Unable to load documents"
          message={error.message}
          action={
            <Button variant="outlined" onClick={() => refetch()} testId="documents-retry">
              Retry
            </Button>
          }
        />
      ) : (
        <Table
          data={documents}
          columns={columns}
          loading={loading}
          enableRowSelection={false}
          hasActiveFilters={!!searchQuery.trim() || categoryFilter !== "all"}
          onClickRow={handleRowClick}
          renderControls={(_table, { onSortClick } = {}) => (
            <TableControls actions={headingActions} onSortClick={onSortClick}>
              <SearchInput
                placeholder="Search Documents"
                query={searchQuery}
                onChange={setSearchQuery}
                onClear={() => setSearchQuery("")}
              />
              <div data-name="category" data-filter-size="custom" className="min-w-[320px]">
                <Select
                  label="Category"
                  className="w-full"
                  data-testid="document-category-filter"
                  value={categoryFilter}
                  options={[
                    { value: "all", label: "All Categories" },
                    ...(categories ?? []).map((option) => ({ value: option.value, label: option.label }))
                  ]}
                  onSelect={setCategoryFilter}
                />
              </div>
            </TableControls>
          )}
          renderNoDataEmptyState={() => (
            <EmptyState title="No Documents" caption="There are no documents for this person." iconName="description" />
          )}
          renderFilteredEmptyState={() => (
            <EmptyState title="No Documents" caption="No documents match these filters." iconName="description" />
          )}
          data-testid="person-documents-table"
        />
      )}
      <DocumentFormDialog
        open={dialog.mode === "create" || dialog.mode === "edit"}
        mode={dialog.mode === "edit" ? "edit" : "create"}
        personId={data?.person?.id}
        pgaId={pgaId || ""}
        document={dialog.mode === "edit" ? dialog.document : undefined}
        categories={categories ?? []}
        onClose={() => setDialog({ mode: "closed" })}
      />
      <DeleteDocumentDialog
        open={dialog.mode === "delete"}
        document={dialog.mode === "delete" ? dialog.document : undefined}
        pgaId={pgaId || ""}
        onClose={() => setDialog({ mode: "closed" })}
      />
      {printTranscript && canPrintTranscript && pgaId ? (
        <Suspense fallback={null}>
          <PrintTranscriptSheet pgaId={pgaId} onComplete={closePrintTranscript} />
        </Suspense>
      ) : null}
    </>
  );
}
